import React, { useEffect, useState } from "react";
import { Globe, Package, Plus, Tags } from "lucide-react";
import { BulkLabelsDialog } from "@/components/BarcodeTools";
import DataTable from "@/components/DataTable";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ProductForm from "@/components/ProductForm";
import { FlaggedBundlesBanner } from "@/components/catalogue/FlaggedBundlesBanner";
import { NewBundleDialog } from "@/components/catalogue/NewBundleDialog";
import { SuggestFiltersDialog } from "@/components/catalogue/SuggestFiltersDialog";
import { StockStatusPill, StockValue } from "@/components/StockStatus";

const GROUP_OPTIONS = ["Accessories", "Parts", "Tubs", "Saunas", "Swim Spas", "Cold Plunges", "Pergolas", "Covanas", "Unclassified"];
const GROUP_LABEL = { Tubs: "Hot Tubs" };
const glabel = (g) => GROUP_LABEL[g] || g;

function GroupBadge({ value, source }) {
  const tone = value === "Unclassified" ? "bg-slate-100 text-slate-500" : "bg-indigo-50 text-indigo-700";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded ${tone}`} data-testid="product-group-badge">
      {glabel(value) || "Unclassified"}{source === "auto" && <span className="opacity-50">· auto</span>}
    </span>
  );
}

function GroupSelect({ row }) {
  const [val, setVal] = useState(row.product_group_source === "manual" ? row.product_group : "");
  const [busy, setBusy] = useState(false);
  const change = async (e) => {
    e.stopPropagation();
    const g = e.target.value;
    setBusy(true);
    try {
      await api.patch(`/products/${row.legacy_id}/group`, { group: g || null });
      setVal(g);
      toast.success("Group updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update group");
    } finally { setBusy(false); }
  };
  return (
    <select value={val} onClick={(e) => e.stopPropagation()} onChange={change} disabled={busy}
      data-testid={`product-group-select-${row.legacy_id}`}
      className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white text-slate-700 max-w-[150px]">
      <option value="">{row.product_group_source === "auto" ? `Auto: ${glabel(row.product_group)}` : "Unclassified"}</option>
      {GROUP_OPTIONS.map((g) => <option key={g} value={g}>{glabel(g)}</option>)}
    </select>
  );
}

export default function ProductsPage({ bundles = false, subscriptions = false }) {
  const [searchParams] = useSearchParams();
  const own = bundles || subscriptions;
  const group = own ? "" : (searchParams.get("group") || "");
  const { user, can, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.is_super || (can && can("products.manage"));
  const [catAccess, setCatAccess] = useState(false);
  useEffect(() => { api.get("/gshop-readiness/access").then((r) => setCatAccess(r.data.catalogue)).catch(() => setCatAccess(false)); }, []);
  const showCat = isPlatformAdmin || catAccess;
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState({});
  const [labelsOpen, setLabelsOpen] = useState(false);
  const togglePick = (r) => setPicked((s) => { const n = { ...s }; if (n[r.legacy_id]) delete n[r.legacy_id]; else n[r.legacy_id] = r; return n; });
  const pickedList = Object.values(picked);
  const isAdmin = user?.is_super || (user?.roles || []).includes("admin");
  const [tableKey, setTableKey] = useState(0);
  const [newBundle, setNewBundle] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const moveAll = async (to) => {
    if (!window.confirm(`Move ALL products in the ${glabel(group)} list into ${glabel(to)}? They will then appear under ${glabel(to)} instead.`)) return;
    try { const r = await api.post("/products/groups/move", { from_group: group, to_group: to, confirm: true }); toast.success(`${r.data.moved} products moved to ${glabel(to)}`); setTableKey((k) => k + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
  };
  const showingArchived = pickedList.length > 0 && pickedList.every((p) => p.archived);
  const archivePicked = async () => {
    const ids = pickedList.map((p) => p.legacy_id);
    if (!window.confirm(showingArchived ? `Restore ${ids.length} product(s) to the product lists?` : `Archive ${ids.length} product(s)? They disappear from product lists, EPOS and pickers; jobs, orders and customers still show them.`)) return;
    try { const r = await api.post("/products/archive", { legacy_ids: ids, archived: !showingArchived }); const w = r.data.website || []; toast.success(`${r.data.changed} product(s) ${showingArchived ? "restored" : "archived"}${w.length ? ` — website bundles ${showingArchived ? "republished" : "moved to the bin"}: ${w.filter((x) => x.status === "ok").length}/${w.length}` : ""}`); setPicked({}); setTableKey((k) => k + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change archive"); }
  };
  const importBundles = async () => {
    if (!window.confirm("Read the /shop/ website now (read-only) and import/update all its bundles? Individual Accessories products are not changed.")) return;
    try {
      await api.post("/catalogue-migration/snapshot/refresh", {}, { timeout: 280000 });
      const r = await api.post("/new-catalogue/import", { confirm: true, bundles_only: true }, { timeout: 180000 });
      toast.success(`${r.data.bundles} bundles imported/updated`); setTableKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };
  const importSubs = async () => {
    if (!window.confirm("Read both websites now (read-only) and import/update all subscription products and payment plans? Original-site ones are labelled Old. Linked filters are kept.")) return;
    try {
      await api.post("/catalogue-migration/snapshot/refresh", {}, { timeout: 280000 });
      const r = await api.post("/new-catalogue/subscriptions/import", { confirm: true }, { timeout: 280000 });
      const o = await api.post("/new-catalogue/subscriptions/import-old", { confirm: true }, { timeout: 280000 });
      toast.success(`${r.data.subscriptions} new-site and ${o.data.subscriptions} old-site subscriptions imported/updated`); setTableKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };
  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(r.data.categories || []));
  }, []);

  const stockCols = [
    { key: "available", label: "Available", align: "right" },
    { key: "on_order", label: "Ordered", align: "right" },
    { key: "total_stock", label: "Stock Remaining", align: "right", render: (v, r) => <StockValue row={r} /> },
    { key: "required", label: "Required", align: "right" },
    { key: "min_stock_level", label: "Min Stock", align: "right" },
    { key: "stock_status", label: "Stock Status", render: (v) => <StockStatusPill status={v} /> },
  ];
  const bundleCols = [
    { key: "sku", label: "SKU", mono: true },
    { key: "selling_price", label: "Price", align: "right", render: (v) => (v ? `£${Number(v).toFixed(2)}` : "—") },
    { key: "active", label: "Status", render: (v) => <span data-testid="bundle-status" className={`rounded-full px-2 py-0.5 text-xs font-semibold ${v ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`}>{v ? "For sale (stock from components)" : "Flagged for correction"}</span> },
  ];
  const subCols = [
    { key: "subscription_site", label: "Website", render: (_, r) => { const old = r.subscription?.site === "old"; return <span data-testid={`sub-site-${r.legacy_id}`} className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${old ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-800"}`}>{old ? "Old" : "New"}</span>; } },
    { key: "subscription", label: "Payment plans", render: (v) => ((v?.plans || []).map((p) => `${p.name} £${Number(p.price || 0).toFixed(2)}`).join(" · ") || "—") },
    { key: "subscription_filters", label: "Filters linked", render: (_, r) => { const n = (r.subscription?.filters || []).length; return <span data-testid={`sub-filters-${r.legacy_id}`} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${n ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{n ? `${n} linked` : "Not linked yet"}</span>; } },
  ];
  const columns = [
    { key: "legacy_id", label: "Product ID", mono: true },
    { key: "title", label: "Title", strong: true, truncate: true, render: (v, r) => (
      <span className="flex items-center gap-2 min-w-0 w-full">
        <input type="checkbox" checked={!!picked[r.legacy_id]} onClick={(e) => e.stopPropagation()} onChange={() => togglePick(r)}
          className="accent-indigo-600 w-4 h-4 shrink-0" title="Select for barcode labels" data-testid={`product-select-${r.legacy_id}`} />
        <span className="truncate min-w-0">{v}</span>
        {r.product_type === "bundle" && <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-700" data-testid={`product-bundle-badge-${r.legacy_id}`}>Bundle</span>}
        {r.catalogue_source === "new_catalogue" && <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">New</span>}
        {r.archived && <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">Archived</span>}
      </span>
    ) },
    { key: "category", label: "Category" },
    ...(subscriptions ? subCols : bundles ? bundleCols : [
      { key: "product_group", label: "Group", render: (v, r) => (canManage ? <GroupSelect row={r} /> : <GroupBadge value={v} source={r.product_group_source} />) },
      ...stockCols]),
  ];

  const filters = [
    { type: "select", param: "category", label: "Category", options: categories.map((c) => ({ value: c, label: c })) },
    ...(own ? [] : [{ type: "select", param: "stock_status", label: "Stock", options: [
      { value: "in_stock", label: "In stock" },
      { value: "low_stock", label: "Low stock" },
      { value: "out_of_stock", label: "Out of stock" },
      { value: "service", label: "Non-stock / service item" },
    ] }]),
    { type: "select", param: "catalogue_source", label: "Source", options: [{ value: "new_catalogue", label: "New catalogue" }, { value: "teamleaf_legacy", label: "Old products" }] },
    { type: "select", param: "archived", label: "Archive", options: [{ value: "true", label: "Show hidden / archived only" }] },
  ];
  if (!group && !own) {
    filters.push({ type: "select", param: "group", label: "Group", options: GROUP_OPTIONS.map((g) => ({ value: g, label: glabel(g) })) });
  }

  const title = subscriptions ? "Subscriptions" : bundles ? "Bundles" : group ? `Products — ${glabel(group)}` : "Stock & Products";

  return (
    <div className="relative">
      <div className="z-10 flex flex-wrap items-center justify-end gap-2 px-4 pt-4 md:absolute md:right-6 md:top-6 md:p-0">
        {pickedList.length > 0 && (
          <>
            <Button variant="outline" className="gap-1.5 bg-white" onClick={() => setLabelsOpen(true)} data-testid="products-print-labels-btn"><Tags className="w-4 h-4" /> Print labels ({pickedList.length})</Button>
            {(isAdmin || can("products.manage")) && <Button variant="outline" className="gap-1.5 bg-white" onClick={archivePicked} data-testid="products-archive-selected-btn">{showingArchived ? "Restore" : "Archive"} ({pickedList.length})</Button>}
            <Button variant="ghost" size="sm" onClick={() => setPicked({})} data-testid="products-clear-selection">Clear</Button>
          </>
        )}
        {showCat && !subscriptions && <Button variant="outline" className="gap-1.5 bg-white" onClick={() => navigate("/products/google-readiness")} data-testid="products-google-readiness-btn"><Globe className="w-4 h-4" /> Google readiness</Button>}
        {isAdmin && (bundles || group === "Accessories") && <Button variant="outline" className="gap-1.5 bg-white" onClick={() => navigate("/products/bundle-migration")} data-testid="products-bundle-migration-btn"><Package className="w-4 h-4" /> New catalogue setup</Button>}
        {isAdmin && bundles && <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700" onClick={() => setNewBundle(true)} data-testid="bundles-new-btn"><Plus className="w-4 h-4" /> New bundle</Button>}
        {isAdmin && subscriptions && <Button variant="outline" className="gap-1.5 bg-white" onClick={() => navigate("/products/subscriptions/due")} data-testid="subscriptions-due-btn"><Package className="w-4 h-4" /> Deliveries due</Button>}
        {isAdmin && subscriptions && <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700" onClick={() => setSuggestOpen(true)} data-testid="subscriptions-suggest-btn"><Plus className="w-4 h-4" /> Suggest filters</Button>}
        {isAdmin && subscriptions && <Button variant="outline" className="gap-1.5 bg-white" onClick={importSubs} data-testid="subscriptions-import-btn"><Package className="w-4 h-4" /> Import subscriptions from website</Button>}
        {isAdmin && bundles && <Button variant="outline" className="gap-1.5 bg-white" onClick={importBundles} data-testid="bundles-import-btn"><Package className="w-4 h-4" /> Import bundles from website</Button>}
        {isAdmin && group && group !== "Unclassified" && (
          <select className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" value="" onChange={(e) => e.target.value && moveAll(e.target.value)} data-testid="products-move-group-select">
            <option value="">Move all to…</option>
            {GROUP_OPTIONS.filter((g) => g !== group && g !== "Unclassified").map((g) => <option key={g} value={g}>{glabel(g)}</option>)}
          </select>
        )}
        {!own && <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setOpen(true)} data-testid="product-new-btn"><Plus className="w-4 h-4" /> New Product</Button>}
      </div>
      <DataTable
        key={subscriptions ? `subs-${tableKey}` : bundles ? `bundles-${tableKey}` : `${group}-${tableKey}`}
        dataset="products"
        title={title}
        icon={Package}
        columns={columns}
        filters={filters}
        params={{ product_type: subscriptions ? "subscription" : bundles ? "bundle" : "single", ...(group ? { group } : {}) }}
        searchPlaceholder={subscriptions ? "Search subscription name…" : bundles ? "Search bundle name, SKU…" : "Search title, ID, category…"}
        rowLink={(r) => `/products/${r.legacy_id}`}
      />
      {bundles && isAdmin && <FlaggedBundlesBanner key={`fb-${tableKey}`} />}
      {suggestOpen && <SuggestFiltersDialog onClose={(saved) => { setSuggestOpen(false); if (saved) { setTableKey((k) => k + 1); } }} />}
      {newBundle && <NewBundleDialog open onClose={() => { setNewBundle(false); setTableKey((k) => k + 1); }} />}
      {open && <ProductForm onClose={() => setOpen(false)} onSaved={() => setOpen(false)} />}
      {labelsOpen && <BulkLabelsDialog products={pickedList} onClose={() => setLabelsOpen(false)}
        onRemove={(id) => setPicked((s) => { const n = { ...s }; delete n[id]; return n; })} />}
    </div>
  );
}
